import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../services/apiServices';
import { useFetch } from '../../hooks/useFetch';
import { Alert, Modal, Spinner } from '../../components/common/UI';
import LocalizedDateField from '../../components/common/DateField';
import { SearchIcon, ShieldIcon, UsersIcon, UserIcon } from '../../components/common/Icons';
import { formatPlanPrice, REGIONS } from '../../utils/plans';
import { useAuth } from '../../contexts/AuthContext';
import { formatLocalizedDate } from '../../utils/dateUtils';
import AuthenticatedImage from '../../components/common/AuthenticatedImage';

const emptySubscriptionForm = {
  billingProvider: 'manual',
  plan: 'basic',
  status: 'active',
  paymentStatus: 'trialing',
  region: 'BR',
  currency: 'BRL',
  startDate: '',
  trialEndsAt: '',
  endDate: '',
};

const toDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const planLabel = (plan, t) => (plan === 'standard' ? t('plans.standard.title') : t('plans.basic.title'));
const subscriptionStatusLabel = (status, t) => t(`plans.statusLabels.${status || 'active'}`, status || 'active');
const paymentStatusLabel = (status, t) => t(`plans.paymentLabels.${status || 'unpaid'}`, status || 'unpaid');
const providerLabel = (provider) => {
  if (provider === 'apple') return 'Apple';
  if (provider === 'stripe') return 'Stripe';
  return 'Manual';
};

const AdminPage = () => {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { data: users, loading, error, refetch } = useFetch(() => adminAPI.getUsers());
  const { data: deletionLogs, refetch: refetchLogs } = useFetch(() => adminAPI.getDeletionLogs());
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);
  const [subscriptionUser, setSubscriptionUser] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState(emptySubscriptionForm);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');

  const list = useMemo(() => users || [], [users]);
  const filtered = useMemo(() => {
    const needle = search.toLowerCase();
    return list.filter((user) =>
      user.name?.toLowerCase().includes(needle) ||
      user.email?.toLowerCase().includes(needle)
    );
  }, [list, search]);

  const totals = useMemo(() => ({
    total: list.length,
    active: list.filter((user) => user.isActive).length,
    standard: list.filter((user) => user.subscription?.plan === 'standard').length,
    basic: list.filter((user) => user.subscription?.plan !== 'standard').length,
  }), [list]);

  const handleBlockToggle = async (adminUser) => {
    setActionLoading(adminUser.id);
    setMessage(null);
    try {
      if (adminUser.isActive) {
        await adminAPI.blockUser(adminUser.id);
        setMessage({ type: 'success', text: t('admin.userBlocked') });
      } else {
        await adminAPI.unblockUser(adminUser.id);
        setMessage({ type: 'success', text: t('admin.userReleased') });
      }
      await refetch();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('admin.userUpdateError') });
    } finally {
      setActionLoading(null);
    }
  };

  const openSubscriptionModal = (adminUser) => {
    const sub = adminUser.subscription || {};
    setSubscriptionUser(adminUser);
    setSubscriptionForm({
      plan: sub.plan || 'basic',
      status: sub.status || 'active',
      paymentStatus: sub.paymentStatus || 'trialing',
      region: sub.region || 'BR',
      currency: sub.currency || 'BRL',
      startDate: toDateInput(sub.startDate),
      trialEndsAt: toDateInput(sub.trialEndsAt),
      endDate: toDateInput(sub.endDate),
      billingProvider: sub.billingProvider || 'manual',
      appleProductId: sub.appleProductId || '',
      appleOriginalTransactionId: sub.appleOriginalTransactionId || '',
      appleEnvironment: sub.appleEnvironment || '',
      appleExpiresAt: toDateInput(sub.appleExpiresAt),
    });
  };

  const saveSubscription = async (event) => {
    event.preventDefault();
    if (!subscriptionUser) return;
    if (subscriptionForm.billingProvider === 'apple') {
      setMessage({ type: 'error', text: 'Assinaturas Apple devem ser gerenciadas pela App Store. Use apenas sincronizar/reprocessar.' });
      return;
    }
    setActionLoading(`${subscriptionUser.id}-subscription`);
    setMessage(null);
    try {
      await adminAPI.updateSubscription(subscriptionUser.id, {
        ...subscriptionForm,
        startDate: subscriptionForm.startDate || null,
        trialEndsAt: subscriptionForm.trialEndsAt || null,
        endDate: subscriptionForm.endDate || null,
      });
      setSubscriptionUser(null);
      setMessage({ type: 'success', text: t('admin.subscriptionUpdated') });
      await refetch();
      if (subscriptionUser.id === user?.id) await refreshUser?.();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('admin.subscriptionUpdateError') });
    } finally {
      setActionLoading(null);
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteUser || deleteReason.trim().length < 3) {
      setMessage({ type: 'error', text: t('admin.deleteReasonRequired') });
      return;
    }
    setActionLoading(`${deleteUser.id}-delete`);
    try {
      await adminAPI.deleteUser(deleteUser.id, deleteReason.trim());
      setMessage({ type: 'success', text: t('admin.userDeleted') });
      setDeleteUser(null);
      setDeleteReason('');
      await refetch();
      await refetchLogs();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('admin.userDeleteError') });
    } finally {
      setActionLoading(null);
    }
  };

  const syncAppleSubscription = async (adminUser) => {
    setActionLoading(`${adminUser.id}-apple-sync`);
    setMessage(null);
    try {
      await adminAPI.syncAppleSubscription(adminUser.id);
      setMessage({ type: 'success', text: 'Assinatura Apple sincronizada.' });
      await refetch();
      if (adminUser.id === user?.id) await refreshUser?.();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Erro ao sincronizar assinatura Apple.' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <Spinner size="lg" />;

  return (
    <div className="mx-auto max-w-[1104px]">
      <div className="mb-7">
        <div className="flex items-center gap-3">
          <ShieldIcon className="h-7 w-7 text-sky-500" />
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t('admin.title')}</h1>
        </div>
        <p className="mt-1 text-base text-slate-600">{t('admin.subtitle')}</p>
      </div>

      {message && <Alert variant={message.type} onClose={() => setMessage(null)} style={{ marginBottom: '1rem' }}>{message.text}</Alert>}
      {error && <Alert variant="error">{error}</Alert>}

      <div className="mb-6 grid gap-4 md:grid-cols-4">
        <Metric label={t('admin.totalUsers')} value={totals.total} />
        <Metric label={t('admin.activeUsers')} value={totals.active} className="text-emerald-600" />
        <Metric label={t('admin.standardUsers')} value={totals.standard} className="text-amber-700" />
        <Metric label={t('admin.basicUsers')} value={totals.basic} />
      </div>

      <label className="relative mb-6 block">
        <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
        <input
          className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-12 pr-4 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-500 focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
          placeholder={t('admin.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
        <div className="flex h-14 items-center gap-2 border-b border-slate-200 px-4">
          <UsersIcon className="h-5 w-5 text-slate-700" />
          <h2 className="font-bold text-slate-900">{t('admin.usersAndSubscribers')}</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1020px] text-sm">
            <thead>
              <tr>
                <Th>{t('admin.user')}</Th>
                <Th>Email</Th>
                <Th>{t('admin.plan')}</Th>
                <Th>{t('admin.status')}</Th>
                <Th>{t('admin.validUntil')}</Th>
                <Th>{t('admin.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((adminUser) => {
                const sub = adminUser.subscription;
                return (
                  <tr key={adminUser.id}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-sky-50 font-bold text-sky-500">
                          {adminUser.avatar ? <AuthenticatedImage source={adminUser.avatar} alt="" className="h-full w-full object-cover" /> : (adminUser.name?.charAt(0)?.toUpperCase() || <UserIcon className="h-5 w-5" />)}
                        </div>
                        <span className="font-bold text-slate-900">{adminUser.name}</span>
                      </div>
                    </Td>
                    <Td>{adminUser.email}</Td>
                    <Td>
                      <Badge value={sub?.plan === 'standard' ? 'standard' : 'basic'}>
                        {planLabel(sub?.plan, t)} · {formatPlanPrice(sub?.region || 'BR', sub?.plan || 'basic', i18n.language)}
                      </Badge>
                      <p className="mt-2 text-xs font-semibold text-slate-500">
                        {providerLabel(sub?.billingProvider)} · {subscriptionStatusLabel(sub?.status, t)} · {paymentStatusLabel(sub?.paymentStatus, t)}
                      </p>
                    </Td>
                    <Td><Badge value={adminUser.isActive ? 'active' : 'blocked'}>{adminUser.isActive ? t('admin.active') : t('admin.blocked')}</Badge></Td>
                    <Td>{formatLocalizedDate(sub?.endDate || sub?.trialEndsAt, i18n.language) || '-'}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <button
                          className="inline-flex h-9 min-w-24 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900 transition hover:border-sky-200 hover:text-sky-600"
                          onClick={() => handleBlockToggle(adminUser)}
                          disabled={actionLoading === adminUser.id}
                          type="button"
                        >
                          {actionLoading === adminUser.id ? t('common.processing') : adminUser.isActive ? t('admin.block') : t('admin.release')}
                        </button>
                        <button
                          className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900 transition hover:border-sky-200 hover:text-sky-600"
                          onClick={() => openSubscriptionModal(adminUser)}
                          type="button"
                        >
                          {t('admin.editSubscription')}
                        </button>
                        {sub?.billingProvider === 'apple' && (
                          <button
                            className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900 transition hover:border-sky-200 hover:text-sky-600"
                            onClick={() => syncAppleSubscription(adminUser)}
                            disabled={actionLoading === `${adminUser.id}-apple-sync`}
                            type="button"
                          >
                            {actionLoading === `${adminUser.id}-apple-sync` ? t('common.processing') : 'Sincronizar Apple'}
                          </button>
                        )}
                        <button
                          className="inline-flex h-9 items-center justify-center rounded-lg border border-rose-200 bg-white px-3 text-sm font-bold text-rose-600 transition hover:bg-rose-50"
                          onClick={() => setDeleteUser(adminUser)}
                          type="button"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-6 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
        <div className="border-b border-slate-200 px-4 py-4">
          <h2 className="font-bold text-slate-900">{t('admin.deletionLogs')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr>
                <Th>{t('admin.user')}</Th>
                <Th>{t('admin.reason')}</Th>
                <Th>{t('admin.plan')}</Th>
                <Th>{t('admin.paymentStatus')}</Th>
                <Th>{t('admin.validity')}</Th>
                <Th>{t('common.date')}</Th>
              </tr>
            </thead>
            <tbody>
              {(deletionLogs || []).map((log) => (
                <tr key={log.id}>
                  <Td>{log.name || '-'}<br /><span className="text-xs text-slate-500">{log.email}</span></Td>
                  <Td>{log.reason || '-'}</Td>
                  <Td>{log.plan ? planLabel(log.plan, t) : '-'} · {log.region || '-'}</Td>
                  <Td>
                    {[log.status ? subscriptionStatusLabel(log.status, t) : null, log.paymentStatus ? paymentStatusLabel(log.paymentStatus, t) : null].filter(Boolean).join(' · ') || '-'}
                  </Td>
                  <Td>{formatLocalizedDate(log.subscriptionStartDate, i18n.language) || '-'} / {formatLocalizedDate(log.subscriptionEndDate || log.subscriptionTrialEndsAt, i18n.language) || '-'}</Td>
                  <Td>{toDateInput(log.deletedAt) || '-'}</Td>
                </tr>
              ))}
              {!deletionLogs?.length && (
                <tr><Td colSpan={6}>{t('admin.noDeletionLogs')}</Td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <SubscriptionModal
        user={subscriptionUser}
        form={subscriptionForm}
        setForm={setSubscriptionForm}
        onClose={() => setSubscriptionUser(null)}
        onSubmit={saveSubscription}
        loading={actionLoading === `${subscriptionUser?.id}-subscription`}
        t={t}
      />

      <Modal isOpen={!!deleteUser} onClose={() => setDeleteUser(null)} title={t('admin.deleteUserTitle')}>
        <div className="space-y-4">
          <p className="text-sm text-slate-700">{t('admin.deleteUserMessage', { name: deleteUser?.name || deleteUser?.email })}</p>
          <textarea
            className="min-h-24 w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
            value={deleteReason}
            onChange={(event) => setDeleteReason(event.target.value)}
            placeholder={t('admin.deleteReasonPlaceholder')}
          />
          <div className="flex justify-end gap-2">
            <button className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold" type="button" onClick={() => setDeleteUser(null)}>{t('common.cancel')}</button>
            <button className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-60" type="button" onClick={confirmDeleteUser} disabled={actionLoading === `${deleteUser?.id}-delete`}>
              {actionLoading === `${deleteUser?.id}-delete` ? t('common.deleting') : t('admin.confirmDeleteUser')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const SubscriptionModal = ({ user, form, setForm, onClose, onSubmit, loading, t }) => (
  <Modal isOpen={!!user} onClose={onClose} title={t('admin.editSubscription')}>
    <form className="space-y-4" onSubmit={onSubmit}>
      <p className="text-sm text-slate-600">{user?.name} · {user?.email}</p>
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        Provedor: <strong>{providerLabel(form.billingProvider)}</strong>
        {form.billingProvider === 'apple' && <p className="mt-1 text-xs text-slate-500">Assinaturas Apple sao somente leitura no admin e devem ser gerenciadas pela App Store.</p>}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <SelectField disabled={form.billingProvider === 'apple'} label={t('admin.plan')} value={form.plan} onChange={(value) => setForm({ ...form, plan: value })} options={[['basic', planLabel('basic', t)], ['standard', planLabel('standard', t)]]} />
        <SelectField disabled={form.billingProvider === 'apple'} label={t('admin.region')} value={form.region} onChange={(value) => setForm({ ...form, region: value, currency: value === 'EU' ? 'EUR' : value === 'US' ? 'USD' : 'BRL' })} options={Object.keys(REGIONS).map((key) => [key, REGIONS[key].name])} />
        <SelectField disabled={form.billingProvider === 'apple'} label={t('admin.status')} value={form.status} onChange={(value) => setForm({ ...form, status: value })} options={['active', 'cancelled', 'expired'].map((value) => [value, subscriptionStatusLabel(value, t)])} />
        <SelectField disabled={form.billingProvider === 'apple'} label={t('admin.paymentStatus')} value={form.paymentStatus} onChange={(value) => setForm({ ...form, paymentStatus: value })} options={['trialing', 'paid', 'past_due', 'cancelled'].map((value) => [value, paymentStatusLabel(value, t)])} />
        <SelectField disabled={form.billingProvider === 'apple'} label={t('admin.currency')} value={form.currency} onChange={(value) => setForm({ ...form, currency: value })} options={['BRL', 'USD', 'EUR'].map((value) => [value, value])} />
        <DateField disabled={form.billingProvider === 'apple'} label={t('plans.startDate')} value={form.startDate} onChange={(value) => setForm({ ...form, startDate: value })} />
        <DateField disabled={form.billingProvider === 'apple'} label={t('plans.demoUntilShort')} value={form.trialEndsAt} onChange={(value) => setForm({ ...form, trialEndsAt: value })} />
        <DateField disabled={form.billingProvider === 'apple'} label={t('plans.expiresAt')} value={form.endDate} onChange={(value) => setForm({ ...form, endDate: value })} />
      </div>
      {form.billingProvider === 'apple' && (
        <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm md:grid-cols-2">
          <ReadOnlyField label="Apple Product ID" value={form.appleProductId || '-'} />
          <ReadOnlyField label="Original Transaction ID" value={form.appleOriginalTransactionId || '-'} />
          <ReadOnlyField label="Apple Environment" value={form.appleEnvironment || '-'} />
          <ReadOnlyField label="Apple expires at" value={form.appleExpiresAt || '-'} />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold" type="button" onClick={onClose}>{t('common.cancel')}</button>
        <button className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-bold text-white disabled:opacity-60" type="submit" disabled={loading || form.billingProvider === 'apple'}>
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  </Modal>
);

const SelectField = ({ label, value, onChange, options, disabled = false }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-semibold text-slate-900">{label}</span>
    <select className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 disabled:text-slate-500" value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled}>
      {options.map(([optionValue, labelValue]) => <option key={optionValue} value={optionValue}>{labelValue}</option>)}
    </select>
  </label>
);

const DateField = ({ label, value, onChange, disabled = false }) => (
  <LocalizedDateField label={label} value={value} onChange={onChange} disabled={disabled} allowClear />
);

const ReadOnlyField = ({ label, value }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-1 break-all font-mono text-xs text-slate-800">{value}</p>
  </div>
);

const Metric = ({ label, value, className = 'text-slate-900' }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-soft">
    <p className="text-sm text-slate-600">{label}</p>
    <p className={`mt-1 text-2xl font-bold ${className}`}>{value}</p>
  </div>
);

const Th = ({ children }) => <th className="bg-slate-100 px-4 py-4 text-left font-semibold text-slate-700">{children}</th>;
const Td = ({ children, colSpan }) => <td colSpan={colSpan} className="border-t border-slate-200 px-4 py-4 text-slate-700">{children}</td>;

const Badge = ({ value, children }) => {
  const classes = {
    active: 'bg-emerald-50 text-emerald-700',
    blocked: 'bg-rose-50 text-rose-700',
    standard: 'bg-amber-50 text-amber-700',
    basic: 'bg-slate-200 text-slate-700',
  };
  return <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${classes[value] || classes.basic}`}>{children}</span>;
};

export default AdminPage;
